/**
 * Step 2: Plan
 * The sales pitch -- simplified to drive a buying decision.
 * Four content cards: Your Audience, What You'll Get, Make It Yours, What Happens Next.
 * "Make It Yours" uses accordion sections for compact optional customization.
 * Auth-aware: adapts SelfServeHeader and PaymentModal for logged-in vs logged-out users.
 */
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import ErrorCard from '@/components/mediaplan/ErrorCard';
import { PlanSkeleton } from '@/components/mediaplan/ScreenSkeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { FileUpload } from '@/components/ui/file-upload';
import { cn } from '@/lib/utils';
import {
  MapPin,
  TrendingUp,
  Zap,
  Users,
  Eye,
  CheckCircle2,
  Sparkles,
  Mic,
  ImagePlus,
  User,
  Type,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
} from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';
import useSafeAuth from '@/hooks/useSafeAuth';
import { toast } from 'sonner';
import PaymentModal from './PaymentModal';

const CHARACTER_ROLES = [
  'Brand Ambassador', 'Store Owner', 'Customer',
  'Employee / Staff', 'Spokesperson', 'Model',
];

const MAX_CHARACTERS = 3;

export default function MediaPlan() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { user } = useSafeAuth();
  const flow = useMediaPlanFlow();

  // Data state -- from flow context or API fallback
  const [allPlans, setAllPlans] = useState(flow.plans || []);
  const [businessData, setBusinessData] = useState(flow.business || null);
  const [voiceOptions, setVoiceOptions] = useState([{ id: 'male', label: 'Male' }, { id: 'female', label: 'Female' }]);
  const [differentiators, setDifferentiators] = useState([]);
  const [dataLoading, setDataLoading] = useState(!flow.plans);
  const [dataError, setDataError] = useState(null);

  const [selectedPlanId, setSelectedPlanId] = useState(planId || flow.selectedPlanId || 'plan-001');
  const [showPayment, setShowPayment] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [budgetLoading, setBudgetLoading] = useState(false);

  // Load data on mount if not in flow context (direct URL access)
  useEffect(() => {
    async function loadData() {
      try {
        setDataLoading(true);
        const [plans, business] = await Promise.all([
          flow.plans || mediaPlanApi.getPlansForBusiness(flow.business?.id || flow.business?.placeId || planId),
          flow.business || mediaPlanApi.getBusinessByPlaceId(planId),
        ]);
        setAllPlans(plans);
        setBusinessData(business);
        flow.setPlans(plans);
        flow.setBusiness(business);
        // Load differentiators for creative direction placeholder
        try {
          const diffs = await mediaPlanApi.getDifferentiators(business.id || business.placeId || planId);
          setDifferentiators(diffs);
        } catch {
          // Non-critical; placeholder text will use generic default
        }
        if (!flow.selectedPlanId && planId) {
          const matchedPlan = plans.find((p) => p.id === planId);
          if (matchedPlan) flow.selectPlan(planId, matchedPlan);
        }
      } catch (err) {
        setDataError(err.message || 'Failed to load plan data');
      } finally {
        setDataLoading(false);
      }
    }
    if (!flow.plans || !flow.business) {
      loadData();
    } else {
      setDataLoading(false);
      // Still load differentiators for placeholder text
      mediaPlanApi.getDifferentiators(flow.business.id || flow.business.placeId || planId)
        .then(setDifferentiators)
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Creative customization state
  const [creativeBrief, setCreativeBrief] = useState('');
  const [voice, setVoice] = useState(null);
  const [pronunciation, setPronunciation] = useState('');
  const [logoFiles, setLogoFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  // Character state (image-based, matches creative studio pattern)
  const [characters, setCharacters] = useState([]);
  const [editingCharIdx, setEditingCharIdx] = useState(null);
  const [charForm, setCharForm] = useState({ name: '', role: '' });
  const [charPreview, setCharPreview] = useState(null);
  const charFileRef = useRef(null);

  const plan = useMemo(
    () => allPlans.find((p) => p.id === selectedPlanId) || allPlans[0],
    [selectedPlanId, allPlans]
  );

  // Sync selected plan to flow context
  useEffect(() => {
    if (plan && plan.id !== flow.selectedPlanId) {
      flow.selectPlan(plan.id, plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id]);

  // Handle "Build Plans" for custom budget
  const handleBuildPlans = useCallback(async () => {
    const budget = parseInt(budgetInput.replace(/[^0-9]/g, ''), 10);
    if (!budget || budget < 100) {
      toast.error('Please enter a valid budget ($100+)');
      return;
    }
    setBudgetLoading(true);
    try {
      const plans = await mediaPlanApi.getPlansForBusiness(
        businessData?.id || businessData?.placeId || planId,
        budget
      );
      setAllPlans(plans);
      flow.setPlans(plans);
      if (plans.length > 0) {
        const rec = plans.find((p) => p.recommended) || plans[0];
        setSelectedPlanId(rec.id);
      }
      toast.success(`${plans.length} plan${plans.length !== 1 ? 's' : ''} built for $${budget.toLocaleString()}`);
    } catch (err) {
      toast.error('Failed to build plans', { description: err.message });
    } finally {
      setBudgetLoading(false);
    }
  }, [budgetInput, businessData, planId, flow]);

  const formatNumber = (n) => {
    if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
    return n.toString();
  };

  const formatRange = (range) => `${formatNumber(range.min)}-${formatNumber(range.max)}`;

  const handleImageUpload = (newFiles) => {
    setImageFiles((prev) => [...prev, ...newFiles].slice(0, 10));
  };

  const handleImageRemove = (_file, index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCharFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCharPreview(reader.result);
      if (editingCharIdx === null) {
        setCharForm({ name: '', role: '' });
        setEditingCharIdx(-1);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [editingCharIdx]);

  const handleCharSave = () => {
    const newChar = {
      id: `char-${Date.now()}`,
      name: charForm.name || 'Unnamed',
      role: charForm.role || '',
      imageUrl: charPreview,
    };
    if (editingCharIdx >= 0) {
      setCharacters((prev) => prev.map((c, i) => i === editingCharIdx
        ? { ...c, name: newChar.name, role: newChar.role, ...(charPreview ? { imageUrl: charPreview } : {}) }
        : c
      ));
    } else {
      setCharacters((prev) => [...prev, newChar]);
    }
    setEditingCharIdx(null);
    setCharForm({ name: '', role: '' });
    setCharPreview(null);
  };

  const handleCharDelete = (index) => {
    setCharacters((prev) => prev.filter((_, i) => i !== index));
  };

  const openCharEdit = (index) => {
    const c = characters[index];
    setCharForm({ name: c.name || '', role: c.role || '' });
    setCharPreview(c.imageUrl || null);
    setEditingCharIdx(index);
  };

  const closeCharForm = () => {
    setEditingCharIdx(null);
    setCharForm({ name: '', role: '' });
    setCharPreview(null);
  };

  // Collect customization before opening payment
  const handleOpenPayment = useCallback(() => {
    flow.setCustomization({
      creativeBrief,
      voice,
      pronunciation,
      characters,
      logoFiles: logoFiles.map((f) => f.name || f),
      imageFiles: imageFiles.map((f) => f.name || f),
    });
    setShowPayment(true);
  }, [flow, creativeBrief, voice, pronunciation, characters, logoFiles, imageFiles]);

  // Handle payment success
  const handlePaymentSuccess = useCallback(async (campaignId) => {
    flow.setCampaignId(campaignId);

    // Submit customization to API
    try {
      await mediaPlanApi.submitCustomization(plan.id, {
        creativeBrief,
        voice,
        pronunciation,
        characters,
      });
    } catch {
      // Non-blocking -- customization save failure shouldn't block navigation
      console.warn('Customization save failed (non-blocking)');
    }

    navigate(`/campaign/${plan.id}/generate`);
  }, [flow, plan, creativeBrief, voice, pronunciation, characters, navigate]);

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName="" user={user} returnTo={flow.returnTo} />
        <div className="max-w-[820px] mx-auto px-3 sm:px-6 lg:px-10 pt-6">
          <PlanSkeleton />
        </div>
      </div>
    );
  }

  if (dataError || !plan) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName="" user={user} returnTo={flow.returnTo} />
        <ErrorCard
          title="Couldn't load your plan"
          message={dataError || 'Plan not found. Try searching for your business again.'}
          onRetry={() => window.location.reload()}
          fallbackPath="/campaign/new"
          fallbackLabel="Search again"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SelfServeHeader businessName={businessData?.name || ''} user={user} returnTo={flow.returnTo} />

      {/* Mobile plan selector -- visible below lg */}
      <div className="lg:hidden border-b border-border bg-card/80 backdrop-blur-sm sticky top-14 sm:top-16 z-30">
        <div className="flex gap-2 px-3 py-2.5 overflow-x-auto scrollbar-none">
          {allPlans.map((p) => (
            <button
              key={p.id}
              className={cn(
                'shrink-0 px-3 py-2 rounded-xl text-left transition-all border min-w-[140px]',
                selectedPlanId === p.id
                  ? 'border-coral bg-coral/5 shadow-[0_0_0_2px_rgba(255,107,107,0.1)]'
                  : 'border-border hover:border-muted-foreground/30'
              )}
              onClick={() => setSelectedPlanId(p.id)}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-xs font-semibold truncate">{p.name}</span>
                {p.recommended && (
                  <Badge className="bg-coral text-white text-[8px] px-1 py-0 hover:bg-coral/90 shrink-0">Best</Badge>
                )}
              </div>
              <div className="text-sm font-bold text-coral">${p.budget.toLocaleString()}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex">
        {/* Sidebar -- desktop only */}
        <aside className="w-[280px] bg-card border-r border-border p-5 hidden lg:flex flex-col gap-3 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto shrink-0">
          <div className="mb-1">
            <h2 className="text-sm font-bold">Strategies</h2>
            <p className="text-xs text-muted-foreground">Built from your business profile.</p>
          </div>

          {allPlans.map((p) => (
            <button
              key={p.id}
              className={cn(
                'p-3 border rounded-xl text-left transition-all cursor-pointer w-full',
                selectedPlanId === p.id
                  ? 'border-coral bg-coral/5 shadow-[0_0_0_3px_rgba(255,107,107,0.1)]'
                  : 'border-border hover:border-muted-foreground/30 hover:shadow-sm'
              )}
              onClick={() => setSelectedPlanId(p.id)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">{p.name}</span>
                {p.recommended && (
                  <Badge className="bg-coral text-white text-[9px] hover:bg-coral/90">Best Fit</Badge>
                )}
              </div>
              <div className="text-base font-bold text-coral">${p.budget.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.duration}</div>
            </button>
          ))}

          <Separator className="my-1" />

          <Card className="bg-muted border-0 shadow-none">
            <CardContent className="py-3">
              <div className="text-xs font-semibold mb-0.5">Have a budget in mind?</div>
              <div className="text-[11px] text-muted-foreground mb-2">We'll build strategies within your range.</div>
              <div className="flex gap-1.5">
                <Input
                  placeholder="$1,000"
                  className="flex-1 h-9 text-sm font-semibold"
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(e.target.value)}
                />
                <Button size="sm" className="h-9 whitespace-nowrap text-xs" onClick={handleBuildPlans} disabled={budgetLoading}>
                  {budgetLoading ? 'Building...' : 'Build Plans'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main content */}
        <div className="flex-1 max-w-[820px] mx-auto px-3 sm:px-6 lg:px-10 pt-4 sm:pt-5 pb-24 sm:pb-28">
          {/* Hero */}
          <div className="mb-4 sm:mb-5 animate-[fadeInUp_0.45s_ease_both]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight mb-0.5">{plan.name}</h1>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-[440px]">
                  Reach {plan.audience.households.toLocaleString()} households near you over {plan.duration}.
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Budget</div>
                <div className="text-2xl sm:text-[28px] font-bold tracking-tight">${plan.budget.toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Card 1: Your Audience */}
          <Card className="mb-3 sm:mb-4 animate-[fadeInUp_0.5s_ease_both_0.06s]">
            <CardHeader className="py-2.5 sm:py-3 px-3 sm:px-6">
              <CardTitle className="text-sm">Your Audience</CardTitle>
              <CardDescription className="text-xs">Who will see your ads and why now</CardDescription>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pt-0">
              {/* Insight */}
              <p className="text-xs sm:text-sm leading-relaxed text-gray-800 mb-1.5">
                {plan.insight.text.split(plan.insight.highlightText).map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && (
                      <span className="bg-gradient-to-r from-emerald-200 to-green-200 px-1 py-px rounded font-semibold text-emerald-800">
                        {plan.insight.highlightText}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </p>
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground mb-4">
                <TrendingUp className="h-3 w-3" />
                {plan.insight.source}
              </div>

              <Separator className="mb-3 sm:mb-4" />

              {/* Map + Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="w-full h-28 sm:h-32 rounded-lg bg-[#EAF2F8] relative overflow-hidden">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                  }} />
                  <div className="absolute inset-0">
                    <div className="absolute w-full h-0.5 bg-black/5 top-1/2" />
                    <div className="absolute h-full w-0.5 bg-black/5 left-1/2" />
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-dashed border-coral bg-coral/[0.06]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-coral shadow-[0_0_0_3px_white,0_0_0_5px_rgba(255,107,107,0.15)]" />
                  <Badge variant="secondary" className="absolute top-2 right-2 text-[10px] shadow-sm bg-white">
                    {plan.audience.radius} mi radius
                  </Badge>
                </div>

                <div>
                  <div className="text-xl sm:text-2xl font-bold tracking-tight">{plan.audience.households.toLocaleString()}</div>
                  <div className="text-[11px] sm:text-xs text-muted-foreground mb-2">Households in your area</div>
                  <div className="flex flex-col gap-1 sm:gap-1.5 mb-2 sm:mb-3">
                    {plan.audience.segments.map((seg) => (
                      <div key={seg.label} className="flex items-center gap-2 text-[11px] text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: seg.color }} />
                        {seg.label}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <MapPin className="h-3 w-3 shrink-0" />
                    Runs heaviest {plan.schedule.peak}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: What You'll Get */}
          <Card className="mb-3 sm:mb-4 animate-[fadeInUp_0.5s_ease_both_0.12s]">
            <CardHeader className="py-2.5 sm:py-3 px-3 sm:px-6">
              <CardTitle className="text-sm">What You'll Get</CardTitle>
              <CardDescription className="text-xs">Projected results for your campaign</CardDescription>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pt-0">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 sm:gap-2.5 mb-3 sm:mb-4">
                <div className="bg-muted rounded-xl p-2.5 sm:p-3.5 text-center">
                  <div className="flex items-center justify-center gap-1 mb-0.5 sm:mb-1">
                    <Eye className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ad Views</span>
                  </div>
                  <div className="text-lg sm:text-2xl font-bold tracking-tight">{formatRange(plan.projections.impressions)}</div>
                  <div className="text-[9px] sm:text-[10px] text-muted-foreground">Total impressions</div>
                </div>
                <div className="bg-teal/5 rounded-xl p-2.5 sm:p-3.5 text-center">
                  <div className="flex items-center justify-center gap-1 mb-0.5 sm:mb-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reached</span>
                  </div>
                  <div className="text-lg sm:text-2xl font-bold tracking-tight text-teal-700">{formatRange(plan.projections.reach)}</div>
                  <div className="text-[9px] sm:text-[10px] text-muted-foreground">Unique households</div>
                </div>
              </div>

              <Separator className="mb-3 sm:mb-4" />

              {/* Key message + video spec */}
              <div className="flex items-start gap-2.5 sm:gap-3">
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg bg-gradient-to-br from-muted to-border relative overflow-hidden shrink-0">
                  <div className="absolute inset-0 bg-gradient-to-br from-coral/[0.08] to-teal/[0.08]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Key Message</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-800 mb-1">"{plan.creative.message}"</div>
                  <div className="text-[10px] sm:text-[11px] text-muted-foreground mb-2">
                    {plan.creative.format} -- {plan.creative.quality} -- {plan.variants} version{plan.variants > 1 ? 's' : ''}
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-teal/10 rounded-lg text-[10px] sm:text-[11px] font-medium text-teal-700">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-teal text-white flex items-center justify-center text-[9px] sm:text-[10px] font-bold shrink-0">
                      {plan.variants}
                    </div>
                    {plan.variants > 1 ? 'Weekday + weekend versions' : 'Tailored to your audience'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Make It Yours -- Accordion */}
          <Card className="mb-3 sm:mb-4 animate-[fadeInUp_0.5s_ease_both_0.18s]">
            <CardHeader className="py-2.5 sm:py-3 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm">Make It Yours</CardTitle>
                  <CardDescription className="text-xs">Customize your ad -- all fields are optional</CardDescription>
                </div>
                <Badge variant="secondary" className="text-[10px] shrink-0">Optional</Badge>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pt-0">
              <Accordion type="multiple" className="w-full">
                {/* Creative Direction */}
                <AccordionItem value="creative" className="border-border">
                  <AccordionTrigger className="py-3 hover:no-underline">
                    <div className="flex items-center gap-1.5">
                      <Type className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold">Creative Direction</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <Textarea
                      className="h-20 text-[13px]"
                      placeholder={differentiators.length > 0
                        ? `Describe the tone, taglines, or what makes your business special.\ne.g., "${differentiators.slice(0, 3).join(', ')}" or your own words.`
                        : 'Describe the tone, taglines, or what makes your business special.'}
                      value={creativeBrief}
                      onChange={(e) => setCreativeBrief(e.target.value)}
                    />
                    <div className="text-[11px] text-muted-foreground mt-1.5">
                      This shapes the script, visuals, and feel of your ad.
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Voice & Pronunciation */}
                <AccordionItem value="voice" className="border-border">
                  <AccordionTrigger className="py-3 hover:no-underline">
                    <div className="flex items-center gap-1.5">
                      <Mic className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold">Voice & Pronunciation</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      <div>
                        <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Voice</div>
                        <div className="flex gap-1.5 max-w-[200px]">
                          {voiceOptions.map((opt) => (
                            <button
                              key={opt.id}
                              className={cn(
                                'flex-1 h-8 rounded-lg text-xs font-medium transition-all cursor-pointer border',
                                voice === opt.id
                                  ? 'border-coral bg-coral/5 text-coral'
                                  : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/40'
                              )}
                              onClick={() => setVoice(voice === opt.id ? null : opt.id)}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Pronunciation Guide</div>
                        <Input
                          className="h-8 text-[13px]"
                          placeholder="e.g., AutoZone = AW-toe-zone"
                          value={pronunciation}
                          onChange={(e) => setPronunciation(e.target.value)}
                        />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Characters */}
                <AccordionItem value="characters" className="border-border">
                  <AccordionTrigger className="py-3 hover:no-underline">
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold">Characters</span>
                      {characters.length > 0 && (
                        <span className="text-[10px] text-muted-foreground">({characters.length}/{MAX_CHARACTERS})</span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <input
                      ref={charFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCharFileSelect}
                    />

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {characters.map((char, i) => (
                        <div key={char.id} className="relative group rounded-lg overflow-hidden border bg-muted/20 aspect-square">
                          {char.imageUrl ? (
                            <img src={char.imageUrl} alt={char.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted/40">
                              <User className="h-6 w-6 text-muted-foreground/40" />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-1.5 pt-5">
                            <p className="text-white text-[10px] font-medium truncate">{char.name || 'Unnamed'}</p>
                            {char.role && (
                              <span className="inline-block bg-white/20 text-white text-[9px] rounded px-1 py-0.5 mt-0.5 truncate max-w-full backdrop-blur-sm">
                                {char.role}
                              </span>
                            )}
                          </div>
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors">
                            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm"
                                onClick={() => openCharEdit(i)}
                              >
                                <Pencil className="h-2.5 w-2.5" />
                              </button>
                              <button
                                className="p-1 rounded-full bg-red-500/80 text-white hover:bg-red-600 transition-colors backdrop-blur-sm"
                                onClick={() => handleCharDelete(i)}
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {characters.length < MAX_CHARACTERS && editingCharIdx === null && (
                        <button
                          onClick={() => charFileRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 hover:bg-coral/5 transition-all flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-coral cursor-pointer"
                        >
                          <Plus className="h-4 w-4" />
                          <span className="text-[9px] font-medium">Add</span>
                        </button>
                      )}
                    </div>

                    {characters.length === 0 && editingCharIdx === null && (
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        Add people to feature in your ad (up to 3)
                      </p>
                    )}

                    {/* Inline character form */}
                    {editingCharIdx !== null && (
                      <div className="mt-2.5 p-3 border border-coral/20 bg-coral/[0.02] rounded-lg animate-[fadeInUp_0.2s_ease]">
                        <div className="flex gap-2.5">
                          <div
                            className="w-16 h-16 rounded-lg bg-muted overflow-hidden shrink-0 cursor-pointer border-2 border-dashed border-muted-foreground/25 hover:border-coral/50 transition-colors flex items-center justify-center"
                            onClick={() => charFileRef.current?.click()}
                          >
                            {charPreview ? (
                              <img src={charPreview} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <ImagePlus className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <Input
                              className="h-7 text-xs"
                              placeholder="Name (e.g., Maria)"
                              value={charForm.name}
                              onChange={(e) => setCharForm((prev) => ({ ...prev, name: e.target.value }))}
                            />
                            <select
                              className="w-full h-7 rounded-md border border-border bg-card px-2 text-xs text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral"
                              value={charForm.role}
                              onChange={(e) => setCharForm((prev) => ({ ...prev, role: e.target.value }))}
                            >
                              <option value="">Select role...</option>
                              {CHARACTER_ROLES.map((role) => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-1.5 mt-2">
                          <Button
                            size="sm"
                            className="flex-1 bg-coral hover:bg-coral/90 text-white h-7 text-[11px]"
                            onClick={handleCharSave}
                            disabled={!charPreview && editingCharIdx < 0}
                          >
                            {editingCharIdx >= 0 ? 'Update' : 'Add Character'}
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={closeCharForm}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Assets */}
                <AccordionItem value="assets" className="border-b-0">
                  <AccordionTrigger className="py-3 hover:no-underline">
                    <div className="flex items-center gap-1.5">
                      <ImagePlus className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold">Logo & Photos</span>
                      {(logoFiles.length > 0 || imageFiles.length > 0) && (
                        <span className="text-[10px] text-muted-foreground">
                          ({logoFiles.length + imageFiles.length} file{logoFiles.length + imageFiles.length !== 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Logo</div>
                        <FileUpload
                          accept="image/*"
                          multiple={false}
                          files={logoFiles}
                          onChange={(files) => setLogoFiles(files)}
                          onRemove={() => setLogoFiles([])}
                          description="PNG, SVG, or JPG"
                          className="[&>div:first-child]:py-3"
                        />
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-muted-foreground mb-1.5">
                          Photos {imageFiles.length > 0 && `(${imageFiles.length}/10)`}
                        </div>
                        <FileUpload
                          accept="image/*"
                          multiple
                          files={imageFiles}
                          onChange={handleImageUpload}
                          onRemove={handleImageRemove}
                          description="Up to 10 business photos"
                          className="[&>div:first-child]:py-3"
                        />
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground italic mt-1.5">
                      No assets? We'll generate visuals from your business listing.
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* Card 4: What Happens Next -- compact inline */}
          <Card className="animate-[fadeInUp_0.5s_ease_both_0.24s]">
            <CardContent className="px-3 sm:px-6 py-3 sm:py-4">
              <div className="text-sm font-semibold mb-2.5 sm:mb-3">What Happens Next</div>
              {/* Desktop: horizontal row */}
              <div className="hidden sm:flex items-start gap-4">
                {[
                  { num: 1, icon: Sparkles, color: 'bg-coral/10', iconColor: 'text-coral', text: 'We create your ad', detail: '~2 min' },
                  { num: 2, icon: CheckCircle2, color: 'bg-teal/10', iconColor: 'text-teal', text: 'You review & approve', detail: '3 retries' },
                  { num: 3, icon: Zap, color: 'bg-muted', iconColor: 'text-foreground', text: 'Goes live on CTV', detail: 'Premium networks' },
                ].map((step, i) => (
                  <div key={step.num} className="flex-1 flex flex-col items-center text-center relative">
                    {i < 2 && (
                      <div className="absolute right-[-10px] top-3.5 w-1 h-1 border-t border-r border-border rotate-45" />
                    )}
                    <div className={cn('w-8 h-8 rounded-full flex items-center justify-center mb-1.5', step.color)}>
                      <step.icon className={cn('h-3.5 w-3.5', step.iconColor)} />
                    </div>
                    <div className="text-[11px] font-medium leading-snug">{step.text}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{step.detail}</div>
                  </div>
                ))}
              </div>
              {/* Mobile: compact horizontal row with smaller icons */}
              <div className="flex sm:hidden items-center gap-2">
                {[
                  { num: 1, icon: Sparkles, color: 'bg-coral/10', iconColor: 'text-coral', text: 'Create ad', detail: '~2 min' },
                  { num: 2, icon: CheckCircle2, color: 'bg-teal/10', iconColor: 'text-teal', text: 'Review', detail: '3 retries' },
                  { num: 3, icon: Zap, color: 'bg-muted', iconColor: 'text-foreground', text: 'Goes live', detail: 'CTV' },
                ].map((step, i, arr) => (
                  <React.Fragment key={step.num}>
                    <div className="flex-1 flex flex-col items-center text-center">
                      <div className={cn('w-7 h-7 rounded-full flex items-center justify-center mb-1', step.color)}>
                        <step.icon className={cn('h-3 w-3', step.iconColor)} />
                      </div>
                      <div className="text-[10px] font-medium leading-tight">{step.text}</div>
                      <div className="text-[9px] text-muted-foreground">{step.detail}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="w-3 h-px bg-border shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 lg:left-[280px] right-0 bg-card border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40">
        <div className="max-w-[820px] mx-auto px-3 sm:px-6 lg:px-10 py-2.5 sm:py-3 flex items-center justify-between gap-3">
          {/* Plan summary */}
          <div className="flex-1 min-w-0">
            <div className="text-xs sm:text-sm text-muted-foreground truncate">
              <span className="font-semibold text-foreground">{plan.name}</span>
              <span className="mx-1 sm:mx-1.5">·</span>
              <span className="font-semibold text-foreground">${plan.budget.toLocaleString()}</span>
              <span className="hidden sm:inline text-muted-foreground"> / {plan.duration}</span>
            </div>
          </div>
          <Button
            className="bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] hover:scale-[1.02] transition-all text-xs sm:text-sm h-9 sm:h-10 px-4 sm:px-6 shrink-0"
            onClick={handleOpenPayment}
          >
            <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Create My Ads
          </Button>
        </div>
      </div>

      {showPayment && (
        <PaymentModal
          plan={plan}
          business={businessData}
          user={user}
          onClose={() => setShowPayment(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}
